import './intervals';

import type { ActionReturnType, GlobalState } from './types';

import { DEFAULT_PLAYBACK_RATE } from '../config';
import { migrateAudioPlaybackRate, readStoredAudioPlaybackRate } from '../util/audioPlaybackRate';
import { IS_MULTIACCOUNT_SUPPORTED } from '../util/browser/globalEnvironment';
import { isCacheApiSupported } from '../util/cacheApi';
import { tryParseDeepLink } from '../util/deepLinkParser';
import { getCurrentTabId, reestablishMasterToSelf } from '../util/establishMultitabRole';
import { initGlobal } from '../util/init';
import { cloneDeep } from '../util/iteratees';
import { isLocalMessageId } from '../util/keys/messageKey';
import { Bundles, loadBundle } from '../util/moduleLoader';
import { getInitialHref, parseLocationHash } from '../util/routing';
import { updatePeerColors } from '../util/theme';
import { initializeChatMediaSearchResults } from './reducers/middleSearch';
import { updateTabState } from './reducers/tabs';
import { replaceTabThreadParam, replaceThreadLocalStateParam } from './reducers/threads';
import { selectThreadLocalStateParam } from './selectors/threads';
import { initSharedState } from './shared/sharedStateConnector';
import { initCache } from './cache';
import {
  addActionHandler, getGlobal, setGlobal,
} from './index';
import { INITIAL_TAB_STATE } from './initialState';
import { markThreadsAwaitingRefresh } from './recentlyViewedThreads';
import { selectTabState } from './selectors';

initCache();

addActionHandler('initShared', async (prevGlobal, actions, payload): Promise<void> => {
  const { force } = payload || {};
  try {
    await initGlobal(force, prevGlobal);
  } finally {
    // After a logout-forced reset, re-initialize the API worker only after `initGlobal`
    // has written the fresh global state. Calling `initApi` earlier races with `initGlobal`'s
    // `setGlobal` call, which would overwrite the `connectionStateReady` update that the
    // worker emits, leaving the login screen's country picker stuck on a loading spinner.
    // Still runs when `initGlobal` fails (e.g. IndexedDB lost its connection): without a worker
    // re-init the auth screen would wait forever.
    if (force) {
      const global = getGlobal();
      if (selectTabState(global, getCurrentTabId())?.isMasterTab) {
        actions.initApi();
      }
    }
  }
});

addActionHandler('init', (global, actions, payload): ActionReturnType => {
  const { tabId = getCurrentTabId(), isMasterTab } = payload || {};

  const initialTabState = cloneDeep(INITIAL_TAB_STATE);
  initialTabState.id = tabId;
  initialTabState.isInviteLinkPending = tryParseDeepLink(getInitialHref())?.type === 'inviteLink';
  const { lastPlaybackRate, isLastPlaybackRateActive } = global.audioPlayer;
  const audioPlaybackRate = migrateAudioPlaybackRate(readStoredAudioPlaybackRate()
    ?? (isLastPlaybackRateActive ? lastPlaybackRate : DEFAULT_PLAYBACK_RATE));
  initialTabState.audioPlayer.playbackRate = audioPlaybackRate;
  initialTabState.audioPlayer.isPlaybackRateActive = audioPlaybackRate !== DEFAULT_PLAYBACK_RATE;
  initialTabState.mediaViewer.playbackRate = global.mediaViewer.lastPlaybackRate;
  if (global.lastIsChatInfoShown) {
    initialTabState.chatInfo = {
      isOpen: true,
    };
  }

  global = {
    ...global,
    byTabId: {
      ...global.byTabId,
      [tabId]: initialTabState,
    },
  };

  if (isMasterTab) {
    initialTabState.isMasterTab = true;
  }

  if (IS_MULTIACCOUNT_SUPPORTED && initialTabState.isMasterTab) {
    initSharedState(global.sharedState);
  }

  Object.keys(global.messages.byChatId).forEach((chatId) => {
    const threadsById = global.messages.byChatId[chatId].threadsById;
    Object.keys(threadsById).forEach((thread) => {
      const threadId = Number(thread);
      const lastViewportIds = selectThreadLocalStateParam(global, chatId, threadId, 'lastViewportIds');
      if (!lastViewportIds?.length) return;
      // Drop only the stale ids whose message is missing from `byId` (a hung/half-reconciled send
      // can leave one dangling). Wiping the whole list on a single miss emptied the entire chat.
      const validViewportIds = lastViewportIds
        .filter((id) => isLocalMessageId(id) || global.messages.byChatId[chatId]?.byId[id]);
      // Ids went missing, so the restored viewport is AHEAD of the restored history. That is the
      // normal state after a quick reload, not a corruption: `lastViewportIds` is flushed to
      // `localStorage` synchronously on pagehide, while the messages it names ride the throttled
      // (`UPDATE_THROTTLE`) IndexedDB writes, which the browser aborts as the page is torn down. So
      // anything that arrived in the last few seconds is named by the viewport but absent from
      // `byId`, and the filter above drops it — which is a message disappearing from the chat on F5.
      //
      // Dropping is still right (a viewport id with no message renders nothing), but it has to be
      // paired with a refetch, and nothing else will order one: the `listedIds` seeded below are
      // non-empty, so `hydrateCachedMessages` returns early and never marks the thread itself, and
      // `loadViewportMessages` then finds every id it holds already local and skips the network.
      // Marking the thread is what closes that hole — it forces exactly one server fetch when the
      // thread is next opened, which restores the dropped messages.
      if (validViewportIds.length !== lastViewportIds.length) {
        markThreadsAwaitingRefresh([{ chatId, threadId }]);
      }
      if (!validViewportIds.length) {
        global = replaceThreadLocalStateParam(global, chatId, threadId, 'lastViewportIds', undefined);
        return;
      }
      if (validViewportIds.length !== lastViewportIds.length) {
        global = replaceThreadLocalStateParam(global, chatId, threadId, 'lastViewportIds', validViewportIds);
      }
      global = initializeChatMediaSearchResults(global, chatId, threadId, tabId);
      global = replaceTabThreadParam(
        global,
        chatId,
        threadId,
        'viewportIds',
        validViewportIds,
        tabId,
      );
    });
  });

  // Temporary state fix
  Object.keys(global.messages.byChatId).forEach((chatId) => {
    const threadsById = global.messages.byChatId[chatId].threadsById;
    const fixedThreadsById = Object.keys(threadsById).reduce((acc, key) => {
      const t = threadsById[key];
      if (!t.localState?.lastViewportIds) {
        acc[key] = t;
        return acc;
      }

      acc[key] = {
        ...t,
        localState: {
          ...t.localState,
          listedIds: t.localState.lastViewportIds,
        },
      };
      return acc;
    }, {} as GlobalState['messages']['byChatId'][string]['threadsById']);

    global = {
      ...global,
      messages: {
        ...global.messages,
        byChatId: {
          ...global.messages.byChatId,
          [chatId]: {
            ...global.messages.byChatId[chatId],
            threadsById: fixedThreadsById,
          },
        },
      },
    };
  });

  const parsedMessageList = parseLocationHash(global.currentUserId);

  if (global.auth.state !== 'authorizationStateReady'
    && !global.passcode.hasPasscode && !global.passcode.isScreenLocked) {
    Object.values(global.byTabId).forEach(({ id: otherTabId }) => {
      if (otherTabId === tabId) return;
      global = updateTabState(global, {
        inactiveReason: 'auth',
      }, otherTabId);
    });
  }

  isCacheApiSupported().then((isSupported) => {
    global = getGlobal();
    global.isCacheApiSupported = isSupported;
    setGlobal(global);
  });

  if (global.peerColors) {
    updatePeerColors(global.peerColors.general);
  }

  return updateTabState(global, {
    messageLists: parsedMessageList ? [parsedMessageList] : initialTabState.messageLists,
  }, tabId);
});

addActionHandler('requestMasterAndCallAction', async (
  global, actions, payload,
): Promise<void> => {
  const { tabId = getCurrentTabId() } = payload;

  if (selectTabState(global, tabId).isMasterTab) {
    const { action, payload: actionPayload } = payload;
    // @ts-ignore
    actions[action](actionPayload);
    return;
  }

  if (global.phoneCall || global.groupCalls.activeGroupCallId) {
    await loadBundle(Bundles.Calls);
    if ('hangUp' in actions) actions.hangUp({ tabId });
    if ('leaveGroupCall' in actions) actions.leaveGroupCall({ tabId });
  } else {
    reestablishMasterToSelf();
  }

  global = getGlobal();
  global = updateTabState(global, {
    multitabNextAction: payload,
  }, tabId);
  setGlobal(global);
});

addActionHandler('clearMultitabNextAction', (global, actions, payload): ActionReturnType => {
  const { tabId = getCurrentTabId() } = payload || {};

  return updateTabState(global, {
    multitabNextAction: undefined,
  }, tabId);
});
